import React, { useCallback, useEffect, useState } from "react";
import {
  loadMultimedia,
  useMultimediaContext,
} from "../../../context/multimediaContext";
import DropDown from "../Inputs/DropDown";
import MultimediaPreview from "./MultimediaPreview";
import Loading from "../Loading";
import { useRouter } from "next/router";
import SearchBar from "../../global/SearchBar/SearchBar";
import Pagination from "../Pagination/Pagination";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { useToast } from "../../../context/toastContext";

const MediaGrid = () => {
  const {
    state: { multimedia, categories, total },
    dispatch: multimediaDispatch,
  } = useMultimediaContext();
  const [category, setCategory] = useState("Latest");
  const [term, setTerm] = useState("");
  const [notFoundTerm, setNotFoundTermTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const toast = useToast();

  const [queryParams, setQueryParams] = useQueryParams({
    term: StringParam,
    category: StringParam,
    page: NumberParam,
    per_page: NumberParam,
  });

  useEffect(() => {
    if (!router.isReady) return;

    const { category, term } = router.query;
    const fetchMultimedia = async () => {
      try {
        setLoading(true);
        const searchTerm = term as string;
        const categoryTerm = category as string;
        await loadMultimedia(
          multimediaDispatch,
          { term: searchTerm, category: categoryTerm, ...queryParams }
        );
        setTerm(searchTerm || " ");
        setCategory(categoryTerm  || searchTerm ? "Choose an option" : "Latest");
        setLoading(false);
      } catch (e) {
        setLoading(false);
        toast("error", "Error loading multimedia. Please try again later.");
      }
    };
    fetchMultimedia();
  }, [router.isReady, router.query.category, router.query.term, router.query.page, router.query.per_page]);

  useEffect(() => {
    const { category } = router.query;
    if (categories?.length > 0 && category) {
      const categoryExists = categories.find((cat) => cat.name === category);
      const categoryName = categoryExists ? (category as string) : "Latest";
      setCategory(categoryName);
    }
  }, [categories, router.isReady]);

  const handleSearch = useCallback(() => {
    setNotFoundTermTerm(term);
    router.push(`/multimedia?term=${term}`);
  }, [term]);

  return (
    <>
      <SearchBar
        hideResults={true}
        className="pb-2 md:pb-4"
        type="Videos"
        value={term}
        onSearch={handleSearch}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const value = event.target.value;
          setTerm(value);
        }}
        autoComplete={true}
      />
      <div>
        <div className="flex items-end pb-4 xs:flex-col lg:flex-row">
          <h1 className="text-xl md:text-3xl font-light w-full leading-1">
            Multimedia
          </h1>
          <div className="w-full lg:w-1/2 2xl:w-1/3 mt-2">
            {categories && (
              <DropDown
                type="line"
                value={category}
                options={categories.map((cat) => cat.name)}
                onOptionSelect={(categorySelected) => {
                  router.push(`/multimedia?category=${categorySelected}`);
                  loadMultimedia(multimediaDispatch, { category: categorySelected });
                  setCategory(categorySelected);
                }}
              />
            )}
          </div>
        </div>
        {!!multimedia && !loading ? (
          multimedia?.length > 0 ? (
            <div className="grid grid-cols-12 gap-4 pb-16">
              {multimedia &&
                multimedia.map((m, idx) => (
                  <MultimediaPreview
                    className="col-span-12 md:col-span-6 lg:col-span-4 2xl:col-span-3"
                    media={m}
                    key={idx}
                  />
                ))}
            </div>
          ) : (
            <p className="text-center pt-32 text-lg">
              {`No videos found for ${
                notFoundTerm.length > 0 ? `"${notFoundTerm}". Please try again` : " "
              }`}
            </p>
          )
        ) : (
          <Loading isLoading={loading} />
        )}
      </div>
      {(
        !loading && multimedia?.length > 0 && term?.length > 0 && total > 25 &&
        <Pagination
          records={total}
          onPageChange={(e) => {
            setQueryParams({
              ...queryParams,
              page: e.page,
              per_page: e.perPage
            });
          }}
        />
      )}
    </>
  );
};

export default MediaGrid;
